import { useEffect, useId, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./AccessibleChartRegion.scss";

const valueToText = (value) => {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getColumns = (data, chart) => {
  const dataColumns = [...new Set(data.flatMap((row) => Object.keys(row || {})))];
  const configuredColumns = (chart.col_desc || [])
    .map(({ column_name }) => column_name)
    .filter((column) => dataColumns.includes(column));
  const columns = [...configuredColumns, ...dataColumns.filter((column) => !configuredColumns.includes(column))];

  return columns.map((key) => ({
    key,
    label: chart.col_desc?.find(({ column_name }) => column_name === key)?.column_title
      || chart.schema_verbose_map?.[key]
      || key,
  }));
};

/* Carbon Charts 1.x reuses IDs inside separate chart instances. */
const AccessibleChartRegion = ({ title, chart = {}, data = [], children }) => {
  const { t } = useTranslation();
  const regionRef = useRef(null);
  const rawId = useId().replace(/:/g, "");
  const headingId = `chart-${rawId}-title`;
  const summaryId = `chart-${rawId}-summary`;
  const rows = useMemo(
    () => (Array.isArray(data) ? data.filter((row) => row && typeof row === "object") : []),
    [data]
  );
  const columns = useMemo(() => getColumns(rows, chart), [rows, chart]);
  const category = chart.x_label || chart.x_column_name || t("charts.category");
  const metric = chart.y_label || chart.y_columns?.[0]?.column_title || chart.y_columns?.[0]?.column_name || t("charts.value");

  useEffect(() => {
    const region = regionRef.current;
    if (!region) return undefined;

    const normalizeChartMarkup = () => {
      region.querySelectorAll(".cds--cc--chart-wrapper").forEach((chartRoot, chartIndex) => {
        const idMap = new Map();
        const prefix = `${rawId}-carbon-${chartIndex}-`;

        chartRoot.querySelectorAll("[id]").forEach((element) => {
          if (!element.id.startsWith(prefix)) {
            const oldId = element.id;
            const newId = `${prefix}${oldId}`;
            idMap.set(oldId, newId);
            element.id = newId;
          }
        });
        chartRoot.querySelectorAll("[aria-labelledby], [aria-describedby]").forEach((element) => {
          ["aria-labelledby", "aria-describedby"].forEach((attribute) => {
            const value = element.getAttribute(attribute);
            if (value) {
              element.setAttribute(
                attribute,
                value.split(/\s+/).map((id) => idMap.get(id) || id).join(" ")
              );
            }
          });
        });
        chartRoot.querySelectorAll(".cds--cc--title").forEach((heading) => {
          heading.setAttribute("aria-hidden", "true");
        });
        chartRoot.querySelectorAll("svg").forEach((svg) => {
          svg.setAttribute("aria-hidden", "true");
          svg.setAttribute("focusable", "false");
        });
      });
    };

    normalizeChartMarkup();
    const observer = new MutationObserver(normalizeChartMarkup);
    observer.observe(region, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [rawId]);

  return (
    <section ref={regionRef} aria-labelledby={headingId} aria-describedby={summaryId} className="accessible-chart-region">
      <h2 id={headingId} className="accessible-chart-region__title">{title}</h2>
      <p id={summaryId} className="accessible-chart-region__summary">
        {t("charts.dataSummary", { count: rows.length, category, metric })}
      </p>
      {children}
      {columns.length > 0 && (
        <div className="accessible-chart-region__data">
          <table>
            <caption>{t("charts.dataTableCaption", { title })}</caption>
            <thead>
              <tr>
                {columns.map(({ key, label }) => <th key={key} scope="col">{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map(({ key }) => <td key={key}>{valueToText(row[key])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AccessibleChartRegion;
